"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleAlert as AlertCircle, Cpu, ShieldCheck, Wind, SlidersHorizontal } from "lucide-react";
import { Card } from "@/components/ui/card";
interface SpecItem {
  id: string;
  title: string;
  description: string;
}
const packageComponents: SpecItem[] = [{
  id: "comp-1",
  title: "Bull & Motion Base",
  description: "The core mechanical unit providing multi-directional bucking and spinning motion."
}, {
  id: "comp-2",
  title: "Controller",
  description: "The operator console with speed adjustments, pre-set programs, and emergency stop controls."
}, {
  id: "comp-3",
  title: "Safety Arena",
  description: "The inflatable protective mat surrounding the bull to cushion rider falls."
}, {
  id: "comp-4",
  title: "Blower & Accessories",
  description: "Continuous air blower to inflate the arena, plus securing straps and basic setup accessories."
}];
export default function BullCost() {
  const shouldReduceMotion = useReducedMotion();
  const animationProps = shouldReduceMotion ? {
    initial: {
      opacity: 1,
      y: 0
    },
    animate: {
      opacity: 1,
      y: 0
    }
  } : {
    initial: {
      opacity: 0,
      y: 14
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true
    },
    transition: {
      duration: 0.45,
      ease: "easeOut"
    }
  };
  return <section id="inflatablemechanicalbullcost" data-nav="light" className="w-full bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 md:px-6 max-w-6xl">
        <motion.div {...animationProps} className="flex flex-col gap-10">
          {/* Header Block */}
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 bg-primary inline-block shrink-0" />
              <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                EQUIPMENT PROCUREMENT &amp; PRICING SPECIFICATION
              </p>
            </div>

            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold uppercase tracking-tight text-foreground">
              Inflatable Mechanical Bull Cost
            </h2>
          </div>

          {/* Primary Package Evaluation Card */}
          <Card className="rounded-lg border border-border bg-card shadow-xs p-6 md:p-8">
            <div className="flex flex-col gap-6">
              <div className="flex items-center justify-between gap-4 border-b border-border pb-4">
                <div className="flex items-center gap-2.5">
                  <SlidersHorizontal className="h-5 w-5 text-primary shrink-0" />
                  <span className="font-mono text-xs uppercase font-semibold tracking-wider text-foreground">
                    Package Evaluation Guide
                  </span>
                </div>
              </div>

              <div className="text-foreground text-base md:text-lg leading-relaxed space-y-4">
                <p>
                  An inflatable mechanical bull package usually needs to be evaluated as a complete system rather than by the bull or inflatable alone. Depending on the listing, the price may cover the powered bull, controller, inflatable safety arena, blower and related equipment, or only part of that setup. Therefore, check exactly what comes with the package before comparing one inflatable mechanical bull price with another. A dedicated Inflatable Mechanical Bulls page will provide deeper product and buying information for this category.
                </p>
              </div>

              <div className="pt-2 border-t border-border flex items-center gap-1.5 text-xs text-muted-foreground font-mono">
                <AlertCircle className="h-4 w-4 text-primary shrink-0" />
                <span>Ask the seller for a full breakdown of what is included before comparing prices.</span>
              </div>
            </div>
          </Card>

          {/* Breakdown Header */}
          <div className="flex flex-col gap-2 pt-2">
            <div className="flex items-center justify-between">
              <h3 className="font-serif text-xl md:text-2xl font-bold uppercase tracking-tight text-foreground">
                Standard Package Hardware Checklist
              </h3>
              <span className="font-mono text-xs text-muted-foreground uppercase hidden sm:inline-block">
                4 Key System Modules
              </span>
            </div>
            <p className="text-sm text-muted-foreground">
              Confirm each module tier before validating quote totals or comparative supplier bids.
            </p>
          </div>

          {/* 4-Item Package Breakdown Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {packageComponents.map((item, i) => <div key={item.id} data-index={i} className="flex flex-col justify-between border border-border bg-card p-5 transition-colors hover:border-primary/60">
                <div className="flex flex-col gap-3">
                  <div className="flex items-start gap-2 pt-1">
                    {i === 0 && <Cpu className="h-5 w-5 text-primary shrink-0 mt-0.5" />}
                    {i === 1 && <SlidersHorizontal className="h-5 w-5 text-primary shrink-0 mt-0.5" />}
                    {i === 2 && <ShieldCheck className="h-5 w-5 text-primary shrink-0 mt-0.5" />}
                    {i === 3 && <Wind className="h-5 w-5 text-primary shrink-0 mt-0.5" />}
                    <h4 className="font-serif text-base font-bold uppercase leading-snug text-foreground">
                      {item.title}
                    </h4>
                  </div>

                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>)}
          </div>

          {/* Footer note */}
          <div className="border border-dashed border-border bg-muted/50 p-4 flex items-center justify-between flex-wrap gap-3 text-xs font-mono text-muted-foreground">
            <span className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-lg bg-primary" />
              Confirm exact equipment specifications directly with the seller before purchasing.
            </span>
          </div>
        </motion.div>
      </div>
    </section>;
}